import React from 'react'
import { Button, Tooltip, Typography } from 'antd'
import { CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons'
import { FormActionBar } from '@pushly/aqe/lib/components'
import { getClassNames } from '../../../_utils/classnames'
import { Readiness } from '../readiness'

type AppMessageBuilderFooterProps = {
    readiness: Readiness
    submitText: string
    /** False when the signed-in user may not send on this domain at all. */
    submitAllowed: boolean
    submitting?: boolean
    onSubmit: () => void
    onCancel: () => void
}

const INTERNAL_USER_BLOCKED = 'Sending is disabled for internal users on this domain.'

/**
 * The submit controls, pinned to the bottom of the form. The form is long
 * enough that its header scrolls away, and a reader who reaches the end needs
 * a way to submit that is not twelve hundred pixels behind them.
 */
export const AppMessageBuilderFooter = ({
    readiness,
    submitText,
    submitAllowed,
    submitting,
    onSubmit,
    onCancel,
}: AppMessageBuilderFooterProps) => {
    const blockedByReadiness = !readiness.ready
    const disabled = blockedByReadiness || !submitAllowed

    const blockingLabel =
        readiness.blockingCount === 1
            ? '1 thing still needs attention'
            : `${readiness.blockingCount} things still need attention`

    const disabledReason = !submitAllowed ? INTERNAL_USER_BLOCKED : blockedByReadiness ? blockingLabel : undefined

    const submitButton = (
        <Button type="primary" disabled={disabled} loading={submitting} onClick={onSubmit}>
            {submitText}
        </Button>
    )

    return (
        <FormActionBar
            sticky={false}
            className={getClassNames('app-message-builder-footer')}
            status={
                <span className="builder-footer-status">
                    {blockedByReadiness ? (
                        <ExclamationCircleFilled className="status-icon status-icon-blocked" />
                    ) : (
                        <CheckCircleFilled className="status-icon status-icon-ready" />
                    )}
                    <Typography.Text type={blockedByReadiness ? undefined : 'secondary'}>
                        {blockedByReadiness ? blockingLabel : 'Everything required is set.'}
                    </Typography.Text>
                </span>
            }
        >
            <Button type="text" onClick={onCancel}>
                Cancel
            </Button>
            {disabledReason ? <Tooltip title={disabledReason}>{submitButton}</Tooltip> : submitButton}
        </FormActionBar>
    )
}
